(() => {
  const gallerySelector = '[data-product-card-gallery]';
  const quickAddSelector = '[data-product-card-quick-add]';
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const mobileHomeMedia = window.matchMedia('(max-width: 749px)');
  let cartPromise;

  const route = (path) => `${(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/')}${path.replace(/^\//, '')}`;

  const setGalleryImage = (gallery, target) => {
    const images = [...gallery.querySelectorAll('[data-product-card-image]')];
    if (!images.length) return;
    const index = Math.max(0, Math.min(images.length - 1, target));
    if (+gallery.dataset.productCardActiveImage === index) return;
    const card = gallery.closest('[data-product-card-root]');
    const dots = [...(card?.querySelectorAll('[data-product-card-dot]') || [])];

    images.forEach((image, imageIndex) => {
      const active = imageIndex === index;
      image.classList.toggle('is-active', active);
      if (active) image.removeAttribute('aria-hidden');
      else image.setAttribute('aria-hidden', 'true');
    });
    dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
    gallery.dataset.productCardActiveImage = String(index);
  };

  const bindGallery = (gallery) => {
    if (gallery.dataset.productCardGalleryBound) return;
    gallery.dataset.productCardGalleryBound = 'true';
    const mobileHomeRail = mobileHomeMedia.matches
      && gallery.closest('[data-native-home]')
      && gallery.closest('[data-scroll-rail]');
    if (mobileHomeRail) {
      const card = gallery.closest('[data-product-card-root]');
      [...gallery.querySelectorAll('[data-product-card-image]')]
        .slice(1)
        .forEach((image) => image.remove());
      card?.querySelectorAll('[data-product-card-dot]').forEach((dot) => dot.remove());
      gallery.dataset.productCardImageCount = '1';
      gallery.dataset.productCardMobileRailStatic = 'true';
    }
    const images = gallery.querySelectorAll('[data-product-card-image]');
    const inRail = Boolean(gallery.closest('[data-scroll-rail]'));
    let swipeStart;
    let suppressClick = false;
    gallery.dataset.productCardImageCount = String(images.length);
    setGalleryImage(gallery, 0);
    if (images.length < 2) return;

    gallery.addEventListener('pointermove', (event) => {
      if (!finePointer.matches || event.pointerType === 'touch') return;
      let logicalRatio = Math.max(0, Math.min(0.999, (event.clientX - gallery.getBoundingClientRect().left) / gallery.clientWidth));
      if (document.documentElement.dir === 'rtl') logicalRatio = 1 - logicalRatio;
      setGalleryImage(gallery, Math.floor(logicalRatio * images.length));
    });
    gallery.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'touch' || !finePointer.matches) return;
      setGalleryImage(gallery, 0);
    });
    gallery.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'touch' || inRail) return;
      swipeStart = {
        x: event.clientX,
        y: event.clientY,
        index: Number(gallery.dataset.productCardActiveImage || 0),
      };
    });
    gallery.addEventListener('pointerup', (event) => {
      if (event.pointerType !== 'touch' || inRail || !swipeStart) return;
      const deltaX = event.clientX - swipeStart.x;
      const deltaY = event.clientY - swipeStart.y;
      const threshold = Math.min(48, Math.max(24, gallery.clientWidth * 0.12));
      const isHorizontalSwipe = Math.abs(deltaX) >= threshold && Math.abs(deltaX) > Math.abs(deltaY);
      if (isHorizontalSwipe) {
        const physicalDirection = deltaX < 0 ? 1 : -1;
        const logicalDirection = document.documentElement.dir === 'rtl'
          ? -physicalDirection
          : physicalDirection;
        setGalleryImage(gallery, swipeStart.index + logicalDirection);
        suppressClick = true;
        window.setTimeout(() => { suppressClick = false; }, 400);
      }
      swipeStart = undefined;
    });
    gallery.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    }, true);
  };

  const setQuickAddState = (form, state) => {
    const button = form.querySelector('[data-product-card-quick-add-button]');
    const label = form.querySelector('[data-product-card-quick-add-label]');
    if (!button) return;
    if (button.dataset.initialDisabled === undefined) {
      button.dataset.initialDisabled = button.disabled ? 'true' : 'false';
    }
    if (label && label.dataset.initialVisuallyHidden === undefined) {
      label.dataset.initialVisuallyHidden = label.classList.contains('visually-hidden') ? 'true' : 'false';
    }

    form.dataset.state = state;
    button.setAttribute('aria-pressed', state === 'in-cart' ? 'true' : 'false');
    button.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    button.disabled = state === 'loading' || (state === 'idle' && button.dataset.initialDisabled === 'true');

    const labels = {
      idle: button.dataset.idleLabel,
      loading: button.dataset.loadingLabel,
      'in-cart': button.dataset.inCartLabel,
    };
    const nextLabel = labels[state] || labels.idle;
    if (label) {
      if (nextLabel) label.textContent = nextLabel;
      if (label.dataset.initialVisuallyHidden === 'true') {
        label.classList.toggle('visually-hidden', state !== 'in-cart');
      }
    }
    if (nextLabel) button.setAttribute('aria-label', nextLabel);
  };

  const markVariantInCart = (variantId) => {
    document.querySelectorAll(`${quickAddSelector} input[name='id']`).forEach((input) => {
      if (String(input.value) === String(variantId)) {
        setQuickAddState(input.closest(quickAddSelector), 'in-cart');
      }
    });
  };

  const readCart = () => {
    if (!cartPromise) {
      cartPromise = fetch(route('cart.js'), { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
          return response.json();
        })
        .catch(() => ({ items: [] }));
    }
    return cartPromise;
  };

  const bindQuickAdd = (form) => {
    if (form.dataset.productCardQuickAddBound) return;
    form.dataset.productCardQuickAddBound = 'true';
    setQuickAddState(form, 'idle');

    const input = form.querySelector("input[name='id']");
    if (input) {
      readCart().then((cart) => {
        if (cart.items?.some((item) => String(item.variant_id) === String(input.value))) {
          setQuickAddState(form, 'in-cart');
        }
      });
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (form.dataset.state === 'in-cart') {
        window.location.assign(route('cart'));
        return;
      }

      const button = form.querySelector('[data-product-card-quick-add-button]');
      if (!button || button.dataset.initialDisabled === 'true') return;

      setQuickAddState(form, 'loading');
      try {
        const response = await fetch(route('cart/add.js'), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: new FormData(form),
        });
        const item = await response.json();
        if (!response.ok) throw new Error(item.description || `Add to cart failed: ${response.status}`);

        cartPromise = undefined;
        markVariantInCart(item.variant_id || input?.value);
        document.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { item, source: 'product-card' },
        }));
      } catch {
        setQuickAddState(form, 'idle');
      }
    });
  };

  const init = (root = document) => {
    if (root.matches?.(gallerySelector)) bindGallery(root);
    root.querySelectorAll?.(gallerySelector).forEach(bindGallery);
    if (root.matches?.(quickAddSelector)) bindQuickAdd(root);
    root.querySelectorAll?.(quickAddSelector).forEach(bindQuickAdd);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('cart:updated', (event) => {
    cartPromise = undefined;
    const variantId = event.detail?.item?.variant_id;
    if (variantId) markVariantInCart(variantId);
  });
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) init(node);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
